import {Component, effect, inject, input, signal} from '@angular/core';
import {AreaChartModule, Color, LineChartModule, NgxChartsModule, ScaleType} from "@swimlane/ngx-charts";
import {lastSeenWithinThreshold} from "src/app/presentation/common/asset-details.component";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {WaterLevelService} from "src/app/domain/services/water-level.service";
import {AssetSampleApiClient} from "src/app/infrastructure/moovement-api/web-api/asset-sample.api-client";
import {ReactiveFormsModule} from "@angular/forms";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {WaterPointSvgComponent} from "src/app/presentation/common/icons/water-point-svg/water-point-svg.component";
import {fourteenDays, sevenDays, thirtyDays, threeDays, twentyFourHours} from "src/app/presentation/common/globals";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {DeviceAssociation, WaterPoint} from "src/app/infrastructure/moovement-api/models/asset";
import {toWaterPointValueColorClass} from 'src/app/domain/assets/water-points/water-point';
import {Samples} from 'src/app/infrastructure/moovement-api/models/samples';
import {timeAgo} from 'src/app/domain/util/timestamps/timestamps';
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {WaterPointsStore} from 'src/app/domain/assets/water-points/water-points.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {Location} from '@angular/common';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {Router} from '@angular/router';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {MapStateService} from 'src/app/presentation/main-view/map-container/map-container-component';
import {DataType} from 'src/app/domain/domain-models';
import {BootstrapService} from 'src/app/infrastructure/init/bootstrap.service';
import {DataSynchronisationService} from 'src/app/infrastructure/data-synchronisation.service';
import {UserStore} from 'src/app/domain/user/user.store';
import {AssetMetadataComponent} from "src/app/presentation/common/asset-metadata/asset-metadata.component";

@Component({
  selector: 'app-water-point-details',
  imports: [
    NgxChartsModule,
    LineChartModule,
    AreaChartModule,
    ReactiveFormsModule,
    GenericButtonComponent,
    WaterPointSvgComponent,
    AssetMetadataComponent
  ],
  templateUrl: './water-point-details.component.html',
  styleUrl: './water-point-details.component.css'
})
export class WaterPointDetailsComponent {

  id = input.required<string>();

  asset = signal<WaterPoint | undefined>(undefined);
  deviceAssociations = signal<DeviceAssociation[]>([]);

  canBeAltered = false;
  canBeDeleted = false;
  loading = true;
  valueColorClass = signal('color-primary-information');
  loadingWaterLevelData = signal(true);
  noWaterLevelDataAvailable = signal(false);

  loadingTemperatureData = signal(true);
  noTemperatureDataAvailable = signal(false);
  temperature = signal<number | undefined>(undefined);
  temperatureUnit = signal<string | undefined>(undefined);
  lastSeenTemperatureMessage = signal<string | undefined>(undefined);

  statusMessage = signal('Last seen: ?');
  statusMessageColorClass = signal('color-danger');

  percentage?: string
  xAxisTicks: string[] = []
  selectedDateRange: number = fourteenDays
  selectedDateRangeName: string = ''
  dateRanges: Map<string, number> = new Map<string, number>()
  selectableDateRanges: string[] = []
  dataset: { name: string, series: { name: string, value: number[] }[] }[] = []
  scheme: Color = {
    name: "",
    selectable: false,
    group: ScaleType.Ordinal,
    domain: ['#8bb8fb', '#0066ff']
  }
  yScaleMin = 0;
  yScaleMax = 100;

  private lastAssetSample = signal<Samples.AssetSample<any> | undefined>(undefined);
  private isLastSeenWithinThreshold = signal(false);

  private deviceAssociationStore = inject(DeviceAssociationStore);
  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private router = inject(Router);
  private userStore = inject(UserStore);
  private waterPointsStore = inject(WaterPointsStore);

  constructor(
    protected dataSubscriptionService: DataSubscriptionService,
    protected viewportService: ViewportService,
    private assetSampleApiClient: AssetSampleApiClient,
    private assetSampleService: AssetSampleApiClient,
    private bootstrapService: BootstrapService,
    private dataSynchronisationService: DataSynchronisationService,
    private mapStateService: MapStateService,
    private waterLevelService: WaterLevelService
  ) {
    this.dateRanges.set('24 Hours', twentyFourHours).set('3 Days', threeDays).set('7 Days', sevenDays).set('14 Days', fourteenDays).set('30 Days', thirtyDays);

    effect(() => {
      this.canBeAltered = this.userStore.canEditPersistable(this.asset());
      this.canBeDeleted = this.userStore.canDeletePersistable(this.asset());
    });

    effect(() => {
      this.loading = this.waterPointsStore.loading();
    });

    effect(() => {
      const lastSample = this.lastAssetSample();
      this.valueColorClass.set(toWaterPointValueColorClass(lastSample));
    });

    effect(() => {
      const assetId = this.id();
      const farm = this.farmStore.farm();
      if (!assetId || !farm) return;

      this.loadTemperature(assetId, farm.address.countryCode);
    });

    effect(() => {
      this.percentage = (this.lastAssetSample()?.sample?.value || 0) + '%'
      if (this.lastAssetSample()) {
        this.noWaterLevelDataAvailable.set(false);
        this.fetchData('14 Days')
      } else {
        this.loadingWaterLevelData.set(false);
        this.noWaterLevelDataAvailable.set(true);
      }
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.asset.set(this.waterPointsStore.entityMap()[assetId]);
    });

    effect(() => {
      const deviceAssociations = this.deviceAssociationStore.assetIdToAssocations().get(this.id()) || [];
      this.deviceAssociations.set(deviceAssociations);
    });

    effect(() => {
      this.createMessage();
    });

    effect(() => {
      const waterPoint = this.asset();
      if (!waterPoint) return;

      const assetSamples = this.waterPointsStore.assetSamples().get(waterPoint);
      if (!assetSamples || assetSamples.length === 0) return;

      const lastAssetSample = assetSamples.at(0);
      this.lastAssetSample.set(lastAssetSample);
      this.isLastSeenWithinThreshold.set(this.lastSeenWithinThreshold(assetSamples));
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      this.dataSubscriptionService.publish(assetId + '.focus', randomUuid());
    });
  }

  protected createMessage() {
    const lastAssetSample = this.lastAssetSample();
    if (!lastAssetSample) {
      this.statusMessage.set("Last measurement: ?");
      return;
    }

    const isSeenWithinThreshold = this.isLastSeenWithinThreshold();
    if (!isSeenWithinThreshold) {
      this.statusMessageColorClass.set("color-danger");
      this.statusMessage.set(`Last measurement: ${timeAgo(new Date(lastAssetSample.sample!.instant!))}`);
    } else {
      this.statusMessageColorClass.set("color-primary-information");
      this.statusMessage.set(`Last measurement: ${timeAgo(new Date(lastAssetSample.sample!.instant!))}`);
    }
  }

  formatY(data: any): string {
    return data + '%';
  }

  fetchData(dateRangeName: string) {
    this.loadingWaterLevelData.set(true);
    this.selectedDateRange = this.dateRanges.get(dateRangeName)!;
    this.selectedDateRangeName = dateRangeName
    this.selectableDateRanges = Array.from(this.dateRanges).filter(dateRange => dateRange[0] != dateRangeName).map(it => it[0])
    const lastSampleInstant = this.lastAssetSample()?.sample?.instant != undefined ? new Date(this.lastAssetSample()?.sample?.instant!) : new Date()
    const lastSampleInstantMinusDateRange = new Date()
    let timeInMilliseconds = lastSampleInstant.getTime();
    timeInMilliseconds -= this.selectedDateRange
    lastSampleInstantMinusDateRange.setTime(timeInMilliseconds);
    this.assetSampleService.getAssetSamplesBetween<number>('WaterLevel', [this.id()!], lastSampleInstantMinusDateRange, lastSampleInstant)
      .subscribe({
        next: (assetSamples) => {
          let yScaleMin = 0;
          let yScaleMax = 100;
          const xAxisTicks: string[] = []
          const series: { name: string, value: number[] }[] = []
          assetSamples.forEach(assetSample => {
            const deviceId = assetSample.sample.deviceId;
            const association = this.deviceAssociationStore.deviceIdToAssociation().get(deviceId);
            const waterLevel = this.waterLevelService.getLevelAsPercentageFor(assetSample, association);
            if (waterLevel > yScaleMax) yScaleMax = waterLevel;
            if (waterLevel < yScaleMin) yScaleMin = waterLevel;
            const date = new Date(assetSample.sample!.instant!);
            if (!xAxisTicks.includes(date.toDateString())) {
              xAxisTicks.push(date.toDateString())
            }
            series.push({
              name: date.toISOString(),
              value: [waterLevel]
            })
          });
          this.xAxisTicks = xAxisTicks;
          this.dataset = [
            {
              name: this.asset()!.name!,
              series: series
            }
          ]
          this.yScaleMin = yScaleMin;
          this.yScaleMax = yScaleMax;
          this.loadingWaterLevelData.set(false);
        },
        error: () => {
          this.loadingWaterLevelData.set(false);
        }
      })
  }

  private loadTemperature(assetId: string, countryCode: string) {
    this.loadingTemperatureData.set(true);
    this.assetSampleApiClient.getLatestAssetSamples<number>('Temperature', [assetId]).subscribe({
      next: temperatureSamples => {
        const lastTemperatureSample = temperatureSamples[0];
        if (lastTemperatureSample) {
          this.setTemperature(lastTemperatureSample.sample.value, countryCode);
          const lastSeenTemperatureDate = new Date(lastTemperatureSample.sample?.instant!);
          this.lastSeenTemperatureMessage.set(timeAgo(lastSeenTemperatureDate));
          this.noTemperatureDataAvailable.set(false);
        } else {
          this.noTemperatureDataAvailable.set(true);
        }
        this.loadingTemperatureData.set(false);
      },
      error: () => this.loadingTemperatureData.set(false)
    });
  }

  private setTemperature(temperature: number, countryCode: string) {
    if (countryCode === 'US') {
      this.temperature.set(temperature * 1.8 + 32);
      this.temperatureUnit.set('°F');
    } else {
      this.temperature.set(temperature);
      this.temperatureUnit.set('°C');
    }
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  lastSeenWithinThreshold(assetSamples: Samples.AssetSample<any>[]) {
    return lastSeenWithinThreshold(assetSamples, 'WaterLevel');
  }

  toggleCustomize(url: string): void {
    if (!this.loading && this.canBeAltered) {
      this.router.navigate([url, this.id()]);
    }
  }

  async delete() {
    const asset = this.asset();
    if (!asset) return;

    if (confirm(`Are you sure you want to delete water point '${asset.name}'?`)) {
      this.loading = true;
      try {
        await this.dataSynchronisationService.dissociateAsset(asset, DataType.waterPoints);
        this.bootstrapService.reloadWaterPoints();
        this.internalNotificationsStore.addNotification("Successfully deleted water point.", InternalNotificationType.SUCCESS, false);
        this.loading = false;
        this.mapStateService.removeLocator();
        this.router.navigate(['/main/information-center']);
      } catch (error) {
        this.internalNotificationsStore.addNotification("Unable to delete water point.", InternalNotificationType.ERROR, false);
        this.loading = false;
      }
    }
  }
}
